import { CircleSlash, Loader2, Play, TriangleAlert, Upload } from "lucide-react"
import * as React from "react"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { bannerFor } from "@/lib/api-error-message"
import { cn } from "@/lib/utils"

import { hasSecondWayToCarryOn, unreadPagesNote } from "./blocker-state"

import type { UnreadPagesDecision } from "./api/mutations"
import type { Review } from "./data/review-data"

/*
   The two blockers that ask something of the reviewer, split from the strip
   that chooses between them. Each renders one question and its answers and
   decides nothing about which question is being asked.
*/

/**
 * The question, and the ways out of it.
 *
 * Shown to a reader who may not answer as well, without the buttons: the API is
 * staff-only, and hiding the block would leave a visibly stopped review with no
 * explanation at all.
 */
export function UnreadPagesPrompt({
  review,
  canAnswer,
  onAnswer,
  pending,
  inFlight,
  error,
}: {
  review: Review
  canAnswer: boolean
  onAnswer: (decision: UnreadPagesDecision) => void
  pending: boolean
  inFlight: UnreadPagesDecision | null
  error: unknown
}): JSX.Element {
  const secondWay = hasSecondWayToCarryOn(review.unreadPagesHaveText)

  /*
     Open on arrival, collapsible once read.

     The question must be impossible to miss — it is the only state that never
     clears itself — but it stands three buttons and four sentences tall, and a
     reviewer who has read it and wants to look at the label first should not
     scroll past it every time. Collapsed still shows the heading and the note,
     so the review never reads as unblocked.
  */
  const [open, setOpen] = React.useState(true)

  return (
    <Alert
      tone="warning"
      /* "you" only where it is true. A viewer is being told why the review
         stopped, not asked to decide, and addressing them as the decider is the
         same wrong promise the buttons would be. */
      title={
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            {canAnswer
              ? "This review stopped to ask you something"
              : "This review stopped to ask a question"}
          </span>
          {/* Beside the heading it governs. The Alert's own action slot renders
              below the body, which puts a control for the whole block at the
              very bottom of it. */}
          <button
            type="button"
            aria-expanded={open}
            onClick={() => {
              setOpen((v) => !v)
            }}
            className="shrink-0 text-caption font-normal underline underline-offset-2 opacity-80 hover:opacity-100"
          >
            {open ? "Hide" : "Show"}
          </button>
        </span>
      }
    >
      <p className="leading-normal">{unreadPagesNote(review)}</p>

      {open && !canAnswer && (
        <p className="mt-3 border-t border-print/20 pt-3 text-caption">
          A reviewer has to answer this before the review can carry on.
        </p>
      )}

      {open && canAnswer && (
        <div className="mt-2.5 space-y-1.5">
          {/* Labelled with the consequence, not the verb: the two directions are
              not symmetrical, and a reviewer choosing between bare verbs is
              choosing blind. */}
          <AnswerButton
            emphasis
            icon={<Play />}
            title="Carry on without those pages"
            detail={
              secondWay
                ? "Leave them out of the comparison. They stay named as unread in the report."
                : "There is nothing on them to compare. They stay named as unread in the report."
            }
            busy={inFlight === "exclude" && pending}
            disabled={pending}
            onClick={() => {
              onAnswer("exclude")
            }}
          />

          {secondWay && (
            <AnswerButton
              icon={<TriangleAlert />}
              title="Carry on with the text already in the file"
              detail="Compare it anyway. It is the text layer we rejected as untrustworthy, not something we read, so anything it produces needs checking against the page."
              busy={inFlight === "include" && pending}
              disabled={pending}
              onClick={() => {
                onAnswer("include")
              }}
            />
          )}

          {!secondWay && (
            /* Indented under the button whose alternative is missing, not
               floating between the two sections, where it read as a caveat on
               stopping. Without it the single button looks like a lost control. */
            <p className="pl-7 text-caption leading-normal text-text-3">
              Those pages carry no text at all, so there is no second way to carry on: keeping them
              and leaving them out compare the same thing.
            </p>
          )}

          {/* Below a rule, under its own heading. A third equal button would be
              picked by weight of position rather than by meaning — and this is
              not a third way to review the label, it is the decision that it
              should not be reviewed as it stands. */}
          <div className="border-t border-print/20 pt-2.5">
            <p className="mb-1.5 text-caption text-text-3">If the file itself is the problem</p>
            <AnswerButton
              icon={<CircleSlash />}
              title="Stop this review"
              detail="Nothing else is compared and nothing more is spent. Upload a corrected file afterwards and it continues as the next version of this label."
              busy={inFlight === "stop" && pending}
              disabled={pending}
              onClick={() => {
                onAnswer("stop")
              }}
            />
          </div>

          {error != null && (
            /* The server's own sentence, through the shared classifier: a 400
               here usually means somebody else answered first, and "try again"
               would be advice that fails identically every time. */
            <p role="alert" className="text-caption leading-normal text-err">
              {bannerFor(error, "this review", "save").body}
            </p>
          )}
        </div>
      )}
    </Alert>
  )
}

/** What a stopped review says, and the one way forward from it. */
export function StoppedNotice({
  review,
  canResubmit,
  onResubmit,
  pending,
  error,
}: {
  review: Review
  canResubmit: boolean
  onResubmit: (file: File) => void
  pending: boolean
  error: unknown
}): JSX.Element {
  const input = React.useRef<HTMLInputElement>(null)

  return (
    <Alert
      tone="neutral"
      title="This review was stopped"
      action={
        canResubmit ? (
          <Button
            size="sm"
            variant="default"
            leadingIcon={pending ? <Loader2 className="animate-spin" /> : <Upload />}
            disabled={pending}
            onClick={() => {
              input.current?.click()
            }}
          >
            Upload a corrected file
          </Button>
        ) : undefined
      }
    >
      {/* The second sentence is what stops this reading as a failure. */}
      <p className="leading-normal">
        {review.ocrNote !== null ? `${review.ocrNote} ` : ""}A reviewer stopped it rather than
        review the file as it stands. Nothing was compared, and nothing is waiting on anybody.
      </p>

      {canResubmit && (
        <>
          <input
            ref={input}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              // Cleared either way, so picking the same filename again still
              // fires `change` — a reviewer who re-exports over the same path
              // would otherwise press the button to no effect.
              e.target.value = ""
              if (file) onResubmit(file)
            }}
          />
          <p className="mt-2 text-caption">
            It is reviewed as the next version of this label, against the same masters.
          </p>
        </>
      )}

      {!canResubmit && (
        <p className="mt-2 text-caption">
          A corrected file is reviewed as the next version of this label, against the same masters.
        </p>
      )}

      {error != null && (
        <p role="alert" className="mt-2 text-caption leading-normal text-err">
          {bannerFor(error, "the corrected file", "save").body}
        </p>
      )}
    </Alert>
  )
}

/** One answer: its consequence in the title, its mechanics underneath. */
function AnswerButton({
  icon,
  title,
  detail,
  busy,
  disabled,
  emphasis,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  detail: string
  busy: boolean
  disabled: boolean
  /**
   * The way forward, weighted against the way out.
   *
   * Not the brand colour: that is reserved for one primary action per screen
   * and this is a choice between two legitimate answers, not a call to action.
   * A firmer border and a white field are enough to say which one carries on.
   */
  emphasis?: boolean
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left",
        emphasis ? "border-border-strong bg-surface shadow-e1" : "border-border bg-surface/70",
        "transition-colors duration-120 ease-out",
        "hover:border-border-strong hover:bg-surface-sunken",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-surface",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      <span className="mt-0.5 shrink-0 text-text-3 [&_svg]:size-4">
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : icon}
      </span>
      <span className="min-w-0">
        <span className="block text-body-sm font-medium text-text">{title}</span>
        <span className="mt-0.5 block text-caption leading-normal text-text-3">{detail}</span>
      </span>
    </button>
  )
}
